import React from 'react';

import { Col, Container, Row, Spinner, Table } from 'react-bootstrap';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table';

import { TablePagination } from '../../../components';
import { money } from '../../../utils';
import { formatSSNO } from '../utils';

import './styles.scoped.css';

const columnClassName = 'align-middle text-nowrap';

// Column order/labels per the CP-750 business spec.
// Accessors match the exact aliased column names returned by sp_cl_cp_bi_401k_sa
// (see centerline-driver-qualifications-ultrastaff-sql/sp_cl_cp_bi_401k_sa.sql) — note
// the proc aliases use spaces (e.g. 'Last Name', 'LOB FICA WAGES'), not underscores.
export const ficaIndicatorColumns = [
  {
    Header: 'Year',
    accessor: 'Year',
    className: columnClassName,
  },
  {
    Header: 'Driver ID',
    accessor: 'Driver ID',
    className: columnClassName,
  },
  {
    Header: 'SSN',
    accessor: 'SSN',
    className: columnClassName,
    Cell: instance => formatSSNO(instance.value) || '',
  },
  {
    Header: 'Last Name',
    accessor: 'Last Name',
    className: columnClassName,
  },
  {
    Header: 'First Name',
    accessor: 'First Name',
    className: columnClassName,
  },
  {
    Header: 'Middle Name',
    accessor: 'Middle Name',
    className: columnClassName,
  },
  {
    Header: 'FICA earnings for period',
    accessor: 'LOB FICA WAGES',
    className: columnClassName,
    // Coerce to Number so react-table sorts numerically regardless of whether the
    // proc returns a string or a number.
    sortType: (rowA, rowB, columnId) => Number(rowA.values[columnId]) - Number(rowB.values[columnId]),
    Cell: instance => money.format(Number(instance.value) || 0),
  },
  {
    Header: 'LOB',
    accessor: 'LOB',
    className: columnClassName,
  },
];

function getSortedIcon(isSorted, isSortedDesc) {
  let iconProp = <FaSort />;
  if (isSorted) {
    iconProp = isSortedDesc ? <FaSortDown /> : <FaSortUp />;
  }
  return iconProp;
}

const FicaIndicatorTable = ({ data, loading }) => {
  const tableData = Array.isArray(data) ? data : [];
  const tableInstance = useTable(
    {
      columns: ficaIndicatorColumns,
      data: tableData,
      initialState: {
        pageSize: 200,
        sortBy: [
          {
            id: 'LOB FICA WAGES',
            desc: true,
          },
        ],
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  const { getTableProps, getTableBodyProps, headerGroups, page, prepareRow } = tableInstance;

  const tableHeaders = headerGroups.map(headerGroup => {
    const headers = headerGroup.headers.map(column => (
      <th {...column.getHeaderProps(column.getSortByToggleProps())}>
        {column.render('Header')}
        <span>{column.canSort ? getSortedIcon(column.isSorted, column.isSortedDesc) : null}</span>
      </th>
    ));
    return <tr {...headerGroup.getHeaderGroupProps()}>{headers}</tr>;
  });

  const tableRows = page.map(row => {
    prepareRow(row);
    const cells = row.cells.map(cell => (
      <td
        {...cell.getCellProps({
          className: cell.column.className,
          style: cell.column.style,
        })}>
        {cell.render('Cell')}
      </td>
    ));

    return <tr {...row.getRowProps()}>{cells}</tr>;
  });

  return (
    <Container fluid>
      <Row className="align-items-center">
        <Col className="d-flex justify-content-center justify-content-md-end">
          <TablePagination tableInstance={tableInstance} />
        </Col>
      </Row>
      <Row>
        <Col className="border border-light rounded shadow-sm">
          <Table hover responsive borderless {...getTableProps()}>
            <thead className="text-nowrap">{tableHeaders}</thead>
            <tbody {...getTableBodyProps()}>{tableRows}</tbody>
          </Table>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
            {loading && <Spinner animation="border" />}
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default FicaIndicatorTable;
